import { useCallback, useEffect, useRef, useState } from 'react';

// Same-tab sync. The native `storage` event only fires in OTHER tabs, so we
// also broadcast a custom event whenever a value changes, and every hook
// instance for that key re-reads. This keeps the always-mounted command
// palette and a listing page (which share the role filter) in step.
const SYNC_EVENT = 'aievals:local-storage';

function readValue<T>(key: string, initial: T): T {
  if (typeof window === 'undefined') return initial;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : initial;
  } catch {
    return initial;
  }
}

export function useLocalStorage<T>(
  key: string,
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => readValue(key, initial));
  const initialRef = useRef(initial);

  // Persist on change and notify the other instances in this tab.
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // ignore quota errors
    }
    window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: key }));
  }, [key, value]);

  // Re-read when another instance (same tab) or another tab changes this key.
  // The JSON-equality guard keeps the broadcast from looping: an instance that
  // already holds the new value keeps its reference and does not re-render.
  useEffect(() => {
    const reread = () => {
      const next = readValue(key, initialRef.current);
      setValue((prev) =>
        JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
      );
    };
    const onCustom = (e: Event) => {
      if ((e as CustomEvent).detail === key) reread();
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === key) reread();
    };
    window.addEventListener(SYNC_EVENT, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SYNC_EVENT, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, [key]);

  const set = useCallback((next: T | ((prev: T) => T)) => {
    setValue((prev) =>
      typeof next === 'function' ? (next as (prev: T) => T)(prev) : next,
    );
  }, []);

  return [value, set];
}
